import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import {
  DigitalSubmissionChoiceWithText,
  PartialBy,
  QuestionMultiChoiceWithText,
  QuestionSingleChoiceWithText,
  QuestionTypeV2,
} from '../../redux/types';
import useDebounce from '../../hooks/useDebounce';
import useQuestionSaveStatus, { QuestionSaveOp } from '../../hooks/survey/useQuestionSaveStatus';

export type Props<
  T extends QuestionSingleChoiceWithText | QuestionMultiChoiceWithText
> = {
  caseId?: number;
  defaultSubmission?: DigitalSubmissionChoiceWithText<T['type']>;
  disabled?: boolean;
  enrollmentId?: number;
  expand?: boolean;
  onSave?: (vals?: PartialBy<DigitalSubmissionChoiceWithText<T['type']>, 'id'>) => Promise<void>;
  question: T;
  sectionId?: number;
  summarizedView?: boolean;
  // remember to pass the callbacks wrapped in useCallback
  getSaveStatus?: (op: QuestionSaveOp, qId: number, caseId?: number) => void;
  manualSave?: boolean;
  saveOnBlur?: boolean;
}

const ChoiceTextQuestionInput = <
  T extends QuestionSingleChoiceWithText | QuestionMultiChoiceWithText
>({
    caseId,
    defaultSubmission,
    disabled,
    enrollmentId,
    expand,
    getSaveStatus,
    onSave,
    question,
    sectionId,
    summarizedView: summarized,
    manualSave,
    saveOnBlur,
  }: Props<T>): JSX.Element => {
  const [saveStatus, setSaveStatus] = useQuestionSaveStatus(question.id, caseId);
  const [error, setError] = useState<number>(); // holds compromised optionId
  const [answer, setAnswer] = useState<DigitalSubmissionChoiceWithText<T['type']>['answer']>(defaultSubmission?.answer || {});
  const [payloadOptions, setPayloadOptions] = useState<T['payload']>();

  useEffect(() => {
    const qPayloadOptions = summarized
      ? question.payload && question.payload
        .filter((po) => !!answer[po.id]?.selected)
      : question.payload;
    setPayloadOptions(qPayloadOptions);
  }, [answer, question, summarized]);

  // reset answer given when changing section or case
  // in the exhibit A view.
  useEffect(() => {
    setSaveStatus('idle');
    setError(undefined);
    setAnswer(defaultSubmission?.answer || {});
  }, [caseId, defaultSubmission?.answer, enrollmentId, sectionId, setSaveStatus]);

  useEffect(() => {
    if (getSaveStatus) {
      getSaveStatus(saveStatus.op, saveStatus.questionId, saveStatus.caseId);
    }
  }, [getSaveStatus, saveStatus]);

  const getDigitalSubmissionObj = useCallback(
    (selectedAnswer: DigitalSubmissionChoiceWithText<T['type']>['answer']): PartialBy<DigitalSubmissionChoiceWithText<T['type']>, 'id'> | undefined => {
      if (sectionId === undefined || enrollmentId === undefined) {
        return undefined;
      }
      if (defaultSubmission) {
        return {
          ...defaultSubmission,
          answer: selectedAnswer,
        };
      }
      return {
        answer: selectedAnswer,
        digitalQuestionId: question.id,
        digitalSectionId: sectionId,
        enrollmentId,
        digitalCaseId: caseId,
        type: question.type,
      };
    }, [caseId, defaultSubmission, enrollmentId, question.id, question.type, sectionId],
  );

  /**
   * checks the DigitalSubmission.answer payload
   * for the options who are checked and their
   * text inputs are required but are empty
   *
   * @param payload digital submission Answer payload
   * @returns optionId whose text input is required to be filled or undefined otherwise
   */
  const isRequiredTextOptionCheckedWithEmptyText = (payload: DigitalSubmissionChoiceWithText<T['type']>['answer']) => {
    const requiredOptionsIds = question
      .payload?.filter((op) => op.textBoxInputRequired && op.textBox).map((op) => op.id);
    const answerRequiredOpId = requiredOptionsIds?.find(
      (opId) => payload[opId]?.selected && !payload[opId]?.text?.trim(),
    );
    if (answerRequiredOpId) {
      return answerRequiredOpId;
    }
    return undefined;
  };

  const persistToDb = async (payload: DigitalSubmissionChoiceWithText<T['type']>['answer']) => {
    const fullPayload = getDigitalSubmissionObj(payload);
    if (onSave && (saveStatus.op !== 'saving')) {
      setSaveStatus('saving');
      onSave(fullPayload).then(() => {
        setSaveStatus('saved');
      });
    }
  };

  const validateAndSave = (payload: DigitalSubmissionChoiceWithText<T['type']>['answer']) => {
    const incompleteId = isRequiredTextOptionCheckedWithEmptyText(payload);
    if (incompleteId) {
      setError(incompleteId);
    } else {
      persistToDb(payload);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (saveStatus.op === 'saving') {
      return;
    }
    const optionId = Number(event.target.name);
    setSaveStatus('idle');
    let payload: DigitalSubmissionChoiceWithText<T['type']>['answer'] = {
      ...answer,
      [optionId]: {
        selected: checked,
      },
    };
    if (question.type === QuestionTypeV2.SingleChoiceWithText) {
      payload = {
        [optionId]: {
          selected: checked,
        },
      };
    }
    const isTnaId = question.payload?.find((p) => p.isTNA)?.id;
    const isTnaSelected = isTnaId
      && (optionId === isTnaId || answer?.[isTnaId]?.selected);
    if (isTnaSelected && question.type === QuestionTypeV2.MultiChoiceWithText) {
      // remove all selected options if tna option is selected
      payload = {
        [isTnaId]: {
          selected: checked,
        },
      };
    }
    setAnswer(payload);
    if (!manualSave) {
      validateAndSave(payload);
    }
  };

  const saveDebounce = useDebounce((payload: DigitalSubmissionChoiceWithText<T['type']>['answer']) => {
    if (manualSave) {
      return;
    }
    validateAndSave(payload);
  }, 1000);

  const saveOnClickHandler = () => {
    if (!manualSave) {
      return;
    }
    validateAndSave(answer);
  };

  const saveOnBlurHandler = () => {
    if (!saveOnBlur) {
      return;
    }
    validateAndSave(answer);
  };

  const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (saveStatus.op === 'saving') {
      return;
    }
    setError(undefined);
    const { value } = e.target;
    const optId = Number(e.target.name);

    const ansPayload: DigitalSubmissionChoiceWithText<T['type']>['answer'] = {
      ...answer,
      [optId]: {
        selected: answer?.[optId].selected || true,
        text: value,
      },
    };

    setAnswer(ansPayload);
    // even we don't do anything with the value, we have to pass it to the debounce function
    // so that it can know to trigger the debounce callback and reset if a new input is made
    // within the delay time
    if (!manualSave && !saveOnBlur) {
      setSaveStatus('debounceStarting');
      saveDebounce(ansPayload);
    }
  };

  return (
    <Box>
      <FormControl fullWidth error={!!error} component="fieldset">
        <Typography fontWeight={500} gutterBottom>{question.text}</Typography>
        <FormGroup>
          {
            payloadOptions && payloadOptions.map((p) => (
              <Box key={p.id}>
                <FormControlLabel
                  key={p.id}
                  control={question.type === QuestionTypeV2.MultiChoiceWithText ? (
                    <Checkbox
                      checked={answer[p.id]?.selected || false}
                      onChange={handleChange}
                      name={p.id.toString()}
                    />
                  ) : (
                    <Radio
                      checked={answer[p.id]?.selected || false}
                      onChange={handleChange}
                      name={p.id.toString()}
                    />
                  )}
                  label={p.text}
                  disabled={disabled}
                />
                {
                  (answer[p.id]?.selected || expand)
                  && p.textBox
                  && (
                    <Box width="100%">
                      <TextField
                        autoComplete="off"
                        placeholder={p.placeholder || ''}
                        value={answer[p.id]?.text || ''}
                        onChange={onTextChange}
                        name={p.id.toString()}
                        disabled={disabled}
                        size="small"
                        sx={{ width: '100%' }}
                        error={error === p.id}
                        onBlur={saveOnBlurHandler}
                        onFocus={() => setSaveStatus('focused')}
                      />
                    </Box>
                  )
                }
              </Box>
            ))
          }
        </FormGroup>
        {
          !!error && (
            <FormHelperText sx={{ fontSize: '1rem' }}>
              <FormattedMessage id="submission.incomplete" />
            </FormHelperText>
          )
        }
        {
          saveStatus.op === 'saving' && saveStatus.caseId === caseId && (
            <FormHelperText>
              <FormattedMessage id="submission.saving" />
            </FormHelperText>
          )
        }
        {
          saveStatus.op === 'saved' && saveStatus.caseId === caseId && (
            <FormHelperText>
              <Typography variant="caption" color="primary.main">
                <FormattedMessage id="submission.saved" />
              </Typography>
            </FormHelperText>
          )
        }
      </FormControl>
      {
        manualSave && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={saveStatus.op === 'saving'}
              onClick={saveOnClickHandler}
              startIcon={saveStatus.op === 'saving' && (
                <CircularProgress sx={{ color: 'primary.dark' }} size={18} />
              )}
            >
              <FormattedMessage id="save" />
            </Button>
          </Box>
        )
      }
    </Box>
  );
};

export default ChoiceTextQuestionInput;
